import React, { useState, useRef, useEffect } from "react";
import "../../assets/styles/visits.scss";
import { Button } from "primereact/button";
import DataGrid from '../../components/common/dataGrid/dataGrid';
import {
  arrageArrayFunc,
  convertToNameLabel,
  generateColumnMetadata,
  convertToSearchField,
  mask,
  Feature,
} from "../../utils/utils";
import ButtonWithSearch from '../../components/common/buttonWithSearch';
import { useParams } from "react-router-dom";
import { commonPostApi } from "../../utils/apiService";
import { API_ENDPOINTS } from "../../utils/apiEndpoints";
import ProfileView from '../../components/common/profileView';
import { nonRequireVisitListField, VisitsTableColumns } from "../../utils/serviceData";
import { useForm } from "react-hook-form";
import { DevTool } from "@hookform/devtools";
import ListTabView from '../../components/common/tabView';
import TabHeaderView from '../../components/common/tabHeaderView';
import ClinicalInformation from "../examination/clinicalInformation";
import EyeExamNew from "../examination/EyeExamNew.jsx";
import PatientTreatment from "../examination/PatientTreatment.jsx";
import AddOrEditPatient from '../../components/features/patients/addOrEditPatient';
import AddVisitsForPatient from "./addVisitsForPatient";
import _ from "lodash";
import PatientDeleteConfirmation from '../../components/features/patients/PatientDeleteConfirmation.jsx';
import PostSurgeryFollowUp from "../examination/postSurgeryFollowUp";
import PaymentDetails from "../payment/PaymentDetails";
import Path from "../../assets/images/Path";
import Spec from "../../assets/images/spec";
import Prescription from "../../assets/images/prescription";
import Surgery from "../../assets/images/surgery";
import Payment from "../../assets/images/payment";
import { useSelector } from "react-redux";
import { SORT_ORDER } from "../../constants/sortOrder";

const Visits = () => {
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm({});
  const camp_id = useSelector((state) => state.common.camp?.camp_details_id);
  const containerRef = useRef(null);
  const menuLeft = useRef(null);
  const [columns, setColumns] = useState([]);
  const [values, setValues] = useState([]);
  const [patient, setPatient] = useState({});
  const [initLoading, setInitLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [examination, setExamination] = useState({});
  const [profileLoading, setProfileLoading] = useState({});
  const [initialVisitId, setInitialVisitId] = useState();
  const [visible, setVisible] = useState({
    editPatient: false,
    addVisit: false,
  });
  const searchFields = [
    "visit_date",
    "patient_visit_code",
    "examiner_name",
    "purpose",
    "examiner_code",
    "status",
  ];
  const { id } = useParams();
  const initialVal = convertToSearchField(searchFields, "");
  const [navigator, setNavigator] = useState({
    page: 1,
    first: 0,
    rows: 10,
    totalRecords: 1,
    sortBy: "patient_visit_code",
    sortDir: SORT_ORDER.ASC,
    searchCriteria: {
      patient_id: id,
      ...initialVal,
    },
  });

  function onPageChange(event) {
    setLoading(true);
    setNavigator((prev) => ({
      ...prev,
      first: event?.first,
      page: event.page + 1,
    }));
    init({
      ...navigator,
      first: event?.first,
      page: event.page + 1,
    });
  }

  const onRowCountChange = (e) => {
    setLoading(true);
    setNavigator((prev) => ({
      ...prev,
      rows: e?.target?.value,
      page: 1,
      first: 0,
    }));
    init({
      ...navigator,
      page: 1,
      first: 0,
      rows: e?.target?.value,
    });
  };

  useEffect(() => {
    // Trigger the scroll when `examination` changes and `view` is true
    if (examination?.view && containerRef.current) {
      containerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [examination]);

  const onRowClick = (event) => {
    setExamination({ id: event?.data?.patient_visit_id, view: true });
  };

  const init = async (navigator, refresh=true) => {
    setInitLoading(true);
    !refresh && setProfileLoading(true);
    let currentObj = {
      query: {
        page: navigator?.page,
        limit: navigator?.rows,
        sortFields: [[navigator?.sortBy, navigator?.sortDir]],
        searchCriteria: navigator?.searchCriteria,
      },
    };

    Promise.all([
      await commonPostApi(API_ENDPOINTS.SEARCH_LOOKUP_DETAILS, {
        lookup_type_name: "VISIT_PURPOSE",
      }),
      await commonPostApi(API_ENDPOINTS.SEARCH_PATIENT_DETAILS, {
        patient_id: id,
      }),
      await commonPostApi(API_ENDPOINTS.SEARCH_PATIENT_VISIT_DETAILS, currentObj),
    ])
      .then(async ([lookupdetails = [], data = {}, visitData = []]) => {
        const lookup = lookupdetails?.data?.data;        
        const initialVisit = lookup?.find(
          (option) => option.name === "Initial Visit"
        )?.lookup_id;
        let visitLookUp = visitData?.data?.data;
        if (visitLookUp?.[0]?.patient_visit_id) {
          setExamination({ id: visitLookUp[0].patient_visit_id, view: false });
        }
        const visitDetails = visitLookUp.map((visit) => {
          if (initialVisit === visit?.purpose) {
            setInitialVisitId(visit?.patient_visit_id);
          }
          const matchingData = lookup?.find(
            (item) => item.lookup_id === visit.purpose
          );
          return {
            ...visit,
            purpose: matchingData ? matchingData.name : null,
          };
        });
        setNavigator((prev) => ({
          ...prev,
          totalRecords: visitData?.data?.total,
          page: visitData?.data?.currentPage,
        }));

        const patientDetails = data?.data?.patient;
        patientDetails.aarogyasri_no = patientDetails["health_insurance_card"];
        patientDetails.aadhaar_no = patientDetails["aadhar_no"];
        const finalList = {};
        [
          "patient_code",
          "gender",
          "dob",
          "aadhaar_no",
          "aarogyasri_no",
          "ration_card_no",
          "address",
        ].forEach((item) => {
          if (item === "patient_code") {
            finalList["patient_iD"] = patientDetails[item];
          } else {
            finalList[item] = patientDetails[item];
          }
        });

        let finalData = convertToNameLabel(finalList, [
          "patient_name",
          "first_name",
          "last_name",
          "profile_picture",
          "district",
          "state",
          "city",
          "pincode",
          "phone_no",
          "updated_by",
          "created_at",
          "created_by",
          "updated_at",
        ]);

        finalData.name = patientDetails["patient_name"];
        finalData.imgUrl = patientDetails?.profile_picture
          ? `${process.env?.REACT_APP_CURRENT_URL}${API_ENDPOINTS.SEARCH_IMAGE_BY_ID(patientDetails?.patient_id)}`
          : null;
        finalData = Object.entries(finalData)
          .map(([label, value]) => {
            if (
              value?.value &&
              ["aadhaar_no", "aarogyasri_no", "ration_card_no"].includes(label)
            ) {
              return { [label]: { ...value, value: mask(value?.value) } };
            } else {
              return { [label]: value };
            }
          })
          ?.reduce((acc, item) => {
            // Check if the item is an array with a single object inside
            const normalizedItem =
              Array.isArray(item) && item.length === 1 ? item[0] : item;
            return { ...acc, ...normalizedItem };
          }, {});

        setPatient({ finalData, originalData: patientDetails });

        setColumns(
          arrageArrayFunc(
            generateColumnMetadata({
              data: visitDetails,
              nonRequireField: nonRequireVisitListField,
              alias: {
                visit_date: "date",
                patient_visit_code: "visit_id",
                examiner_name: "examiner",
                examiner_code: "examiner_id",
                purpose: "type",
              },
              defaultColumnsArr: VisitsTableColumns
            }),
            [
              "visit_date",
              "patient_visit_code",
              "examiner_name",
              "examiner_code",
              "purpose",
              "status",
            ]
          )
        );
        setValues(visitDetails);
        setLoading(false);
        setInitLoading(false);
        setProfileLoading(false);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const onSortClick = (event) => {
    setLoading(true);
    const sortOrder = event?.sortOrder === 1 ? SORT_ORDER.ASC : SORT_ORDER.DESC;
    setNavigator((prev) => ({
      ...prev,
      sortBy: event?.sortField,
      sortDir: sortOrder,
      sortFields: [[event?.sortField, sortOrder]],
    }));
    init({
      ...navigator,
      sortBy: event?.sortField,
      sortDir: sortOrder,
      sortFields: [[event?.sortField, sortOrder]],
    });
  };

  function handleSearchChange(value) {
    const data = convertToSearchField(searchFields, value);
    setNavigator((prev) => ({
      ...prev,
      searchCriteria: {
        ...data,
        patient_id: id,
      },
    }));
    init({
      ...navigator,
      searchCriteria: {
        ...data,
        patient_id: id,
      },
    });
  }

  const button = [
    {
      type: "primarySuccessButton",
      label: "Add Visit",
      icon: "pi pi-plus",
      class: "mt-2 w-full flex pr-3",
      onClick: () => {
        setVisible((prev) => ({
          ...prev,
          addVisit: true,
        }));
      },
    },
  ];
  const itemRenderer = (item) => (
    <div className="p-menuitem-content">
      {item.label === "Add visit" && (
        <Button
          className="card-button-add"
          icon="pi pi-plus"
          label="Add Visit"
        />
      )}
      {item.label === "Edit" && (
        <Button className="card-button-edit" label="Edit" />
      )}
      {item.label === "Export" && (
        <Button className="card-button-edit" label="Export" />
      )}
    </div>
  );
  const buttonEdit = [
    {
      type: "primarySuccessButton",
      label: "Edit",
      class: "card-button-edit",
      onClick: () => {
        setVisible((prev) => ({ ...prev, editPatient: true }));
      },
    },
    // {
    //   type: "primaryCancelButton",
    //   label: "Delete",
    //   class: "card-button-delete",
    //   onClick: () => {
    //     setVisible((prev) => ({ ...prev, deletePatient: true }));
    //   },
    // },
  ];
  useEffect(() => {
    init(navigator);
    setExamination({});
  }, [camp_id]);

  return (
    <div className="Visits__container">
      {/* <div className="Visits__search-container">
        <Search />
      </div> */}
      <ProfileView
        profile={patient?.finalData}
        button={
          Feature?.featureVerify(Feature.listOfFeature.patientVisitEdit) &&
          buttonEdit
        }
        loading={profileLoading}
        parentClass={"ml-auto"}
      />
      <DataGrid
        header={
          <ButtonWithSearch
            buttons={button}
            searchHeaderLable={"Visits"}
            isSearchEnabled={true}
            placeholder={"Search Visits"}
            isCalenderEnabled={false}
            isButtonEnabledLeft={false}
            isButtonEnabledRight={Feature?.featureVerify(
              Feature.listOfFeature.patientVisitAdd
            )}
            searchPlaceHolder={"Search visits"}
            loading={initLoading}
            styles={{ gridTemplateColumns: "1fr 400px" }}
            onChange={handleSearchChange}
          />
        }
        onSortClick={onSortClick}
        onRowCountChange={onRowCountChange}
        onRowClick={onRowClick}
        onPageChange={onPageChange}
        isPaginatorEnabled={true}
        navigator={navigator}
        column={columns}
        value={values}
        ref={menuLeft}
        loading={loading}
        sortable={
          (values && !_.isArray(values)) || (values && values?.length !== 0 || values?.length === 0)
        }
      />
      {visible?.addVisit && (
        <AddVisitsForPatient
          patientId={id}
          visible={visible?.addVisit}
          setVisible={(bool) => {
            setVisible((prev) => ({ ...prev, addVisit: bool }));
            init(navigator, true);
          }}
        />
      )}
      {visible?.deletePatient && (
        <PatientDeleteConfirmation
          visible={visible?.deletePatient}
          patient={patient?.originalData}
          setVisible={(bool) =>
            setVisible((prev) => ({ ...prev, deletePatient: bool }))
          }
        />
      )}
      {visible?.editPatient && (
        <AddOrEditPatient
          patient={patient}
          visible={visible?.editPatient}
          mode="edit"
          id={id}
          setVisible={(bool, refresh) => {
            setVisible((prev) => ({ ...prev, editPatient: bool }));
            if (refresh) init(navigator,!refresh);
          }}
        />
      )}
      <DevTool control={control} />
      {examination?.id && (
        <div ref={containerRef}>
          <ListTabView
            index={4}
            tabClass={"payement-details-tab"}
            defaultIndex={0}
            listComponent={[
              {
                header: (
                  <TabHeaderView
                    iconHtml={
                      <span className="pr-2">
                        <Path />
                      </span>
                    }
                    title={"Clinical Information"}
                  />
                ),
                content: (
                  <ClinicalInformation patientVisitId={examination?.id} />
                ),
              },
              {
                header: (
                  <TabHeaderView
                    iconHtml={
                      <span className="pr-2">
                        <Spec />
                      </span>
                    }
                    title={"Eye Exam"}
                  />
                ),
                content: <EyeExamNew patientVisitId={examination?.id} />,
              },
              {
                header: (
                  <TabHeaderView
                    iconHtml={
                      <span className="pr-2">
                        <Prescription />
                      </span>
                    }
                    title={"Treatment"}
                  />
                ),
                content: (
                  <PatientTreatment
                    patientVisitId={examination?.id}
                    initialId={initialVisitId}
                  />
                ),
              },
              {
                header: (
                  <TabHeaderView
                    iconHtml={
                      <span className="pr-2">
                        <Surgery />
                      </span>
                    }
                    title={"Post Surgery Follow-Up"}
                  />
                ),
                content: (
                  <PostSurgeryFollowUp patientVisitId={examination?.id} />
                ),
              },
              {
                header: (
                  <TabHeaderView
                    iconHtml={
                      <span className="pr-2">
                        <Payment />
                      </span>
                    }
                    title={"Payment Details"}
                  />
                ),
                content: (
                  <PaymentDetails
                    patientVisitId={examination?.id}
                    patientId={id}
                  />
                ),
              },
            ]}
          />
        </div>
      )}
    </div>
  );
};
export default Visits;
